/**
 * Out-of-order response guarding for list endpoints.
 *
 * Rapid parameter changes (filters, scope, paging, search) fire overlapping
 * requests. Responses can land in any order, so a slower earlier request can
 * overwrite a newer one's results — or, if it rejects, clear results the newer
 * one already applied. Each request carries an id; anything that isn't the
 * latest is dropped.
 *
 * The id's representation is private to this module. Nothing outside it should
 * mint, read or compare one.
 */

let counter = 0;

/**
 * Hands out an id for a new request. It simply counts upwards, and the count is
 * shared by every reducer, so the same id is never given out twice.
 */
export const nextRequestId = () => {
  counter += 1;
  return counter;
};

type LatestRequestConfig = {
  /** Action opening a request. Must carry `payload.requestId`. */
  start: string;
  /** Actions closing it (success + fail). Must carry `payload.requestId`. */
  settle: string[];
  /** Record key to clear when a superseded response is dropped. */
  loadingKey: string;
  /** Record key holding the in-flight id. Must exist on the wrapped Record. */
  key?: string;
};

export const withLatestRequest =
  ({ start, settle, loadingKey, key = 'requestId' }: LatestRequestConfig) =>
  (reducer) =>
  (state, action) => {
    // On initial load, let the wrapped reducer build its initial state.
    if (state === undefined) return reducer(state, action);

    // Record the id we're now waiting on, then let the wrapped reducer handle
    // the action as it always did (loading flag, offset reset, and so on).
    if (action.type === start) {
      return reducer(state, action).set(key, action.payload.requestId);
    }

    // A response for an id we're no longer waiting on belongs to a superseded
    // request: stop loading, change nothing else.
    if (settle.includes(action.type) && action.payload.requestId !== state.get(key)) {
      return state.set(loadingKey, false);
    }

    return reducer(state, action);
  };
